/**
 * The ONE reading of an identity document's extracted metadata (MAR-176).
 *
 * An identity document's `items.extracted_metadata` is the MRZ result verbatim (`persistImagePacket`
 * writes `JSON.stringify(packet.mrz)`). Four places used to re-read that bag, each with its own
 * idea of what it meant:
 *
 *  - `replacePartyStep.ts` derived passport-vs-national-id off the document code — correct;
 *  - `briefs/index.ts` derived `'DUI'` from the *classification*, a value not even in the id-type
 *    vocabulary any more, and asserted it for every Salvadoran-national document on earth;
 *  - `buildDocumentView` re-read the raw keys through its own per-field switch;
 *  - the document page dumped the bag as-is.
 *
 * They now all call `identityFrom`. The important part is the return type: **`null` means "this
 * document carries no readable identity data"**, and that is the only place that question is
 * answered. It used to be a classification (`sv-dui-back` vs `sv-dui-front`), which conflated "the
 * photographed side has no MRZ" with "this is a different kind of document" and, being assigned by
 * a vision model, was wrong in both directions. A front, a glare-ruined scan and a passport whose
 * MRZ failed its check digits are all `identity` documents that yielded nothing — the picker, the
 * run form and the chat agent can now say exactly that.
 */
import type { EntityIdType } from './vocabulary'
import { idTypeName, toAlpha2 } from './vocabulary'

/** The identity a document carries, on the same axes as a canonical entity (MAR-129). */
export interface DocumentIdentity {
  /** What sort of identifier — read off the MRZ document code, never guessed from the country. */
  idType: EntityIdType
  /** Who issued it, ISO 3166-1 alpha-2; null when the MRZ omits it (never defaulted). */
  jurisdiction: string | null
  /** The identifier itself. May be "" when only a name could be read. */
  documentNumber: string
  firstName: string
  lastName: string
  /** `firstName + " " + lastName`, trimmed. */
  fullName: string
  /** The holder's nationality, alpha-2 — a different fact from `jurisdiction`. */
  nationality: string | null
  /** Raw MRZ sex code (`M` / `F` / `X`), "" when absent. */
  sex: string
  /** Birth date as the MRZ stated it (`YYMMDD`) or as ISO, depending on the scanner. */
  birthDate: string
  /** Expiry as stated, "" when absent. */
  expirationDate: string
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

/**
 * Read an identity out of a document's `extracted_metadata`.
 *
 * Returns `null` when the bag holds neither a name nor a document number — i.e. nothing that could
 * identify a person. Callers should treat that as "ask for a readable image", never as an error.
 */
export function identityFrom(raw: unknown): DocumentIdentity | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>

  const firstName = str(data.firstName)
  const lastName = str(data.lastName)
  const documentNumber = str(data.documentNumber)
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()

  if (!fullName && !documentNumber) return null

  // ICAO 9303: the document code's first letter is the document class — 'P' is a passport, and
  // identity cards / residence permits use 'I', 'A' or 'C'. That is the only fact in the MRZ that
  // says WHAT the document is, so it is the only thing consulted. The issuing state says WHERE, and
  // is deliberately not allowed to influence the type: reading 'SLV' as "therefore a DUI" is how a
  // Salvadoran passport ended up filed as a national ID card.
  const documentCode = str(data.documentCode).toUpperCase()
  const idType: EntityIdType = documentCode.startsWith('P') ? 'passport' : 'national_id'

  return {
    idType,
    // The ISSUING state, not the nationality — a Salvadoran may hold a document issued elsewhere,
    // and the identifier belongs to whoever issued it. Unknown stays null rather than falling back.
    jurisdiction: toAlpha2(str(data.issuingState)),
    documentNumber,
    firstName,
    lastName,
    fullName,
    nationality: toAlpha2(str(data.nationality)),
    sex: str(data.sex).toUpperCase(),
    birthDate: str(data.birthDate),
    expirationDate: str(data.expirationDate),
  }
}

/**
 * The document's own name, for prose — "Pasaporte", "Documento de identidad". Country-neutral by
 * decision; see `idTypeName`. This is what a workflow template interpolates as
 * `${slot.documentLabel}` in place of the hardcoded "Documento Único de Identidad" that every
 * seeded instrument used to assert regardless of what the party actually presented.
 */
export function identityDocumentLabel(identity: DocumentIdentity, locale: 'es' | 'en' = 'es'): string {
  return idTypeName(identity.idType, locale)
}
